import { type ReactElement, useEffect, useState } from 'react';
import type {
  AccountDeviceSession,
  OAuthProviderName,
  WorkspaceContract,
  WorkspaceInviteContract,
  WorkspaceMemberContract,
} from '@nest-react/domain';
import { useRegionalApi } from '~/app/region';
import { ACTIVE_REGION_NAME_I18N_KEY } from '~/app/regionLabel';
import { apiRequest, useAuth, useWorkspace } from '~/app/providers';
import { Badge, Button, Select, TextField } from '~/components/ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const MembersAndInvitesPanel = (): ReactElement => {
  const { t } = useTranslation('account');
  const { activeWorkspace } = useWorkspace();
  const { account } = useAuth();
  const [members, setMembers] = useState<WorkspaceMemberContract[]>([]);
  const [invites, setInvites] = useState<WorkspaceInviteContract[]>([]);
  const [inviteRole, setInviteRole] = useState<'ADMIN' | 'MEMBER'>('MEMBER');
  const [latestLink, setLatestLink] = useState('');
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState<
    | { kind: 'transfer'; member: WorkspaceMemberContract }
    | { kind: 'remove'; member: WorkspaceMemberContract }
    | { kind: 'revoke'; invite: WorkspaceInviteContract }
    | null
  >(null);
  const canManage =
    activeWorkspace?.role === 'OWNER' || activeWorkspace?.role === 'ADMIN';
  const load = async (): Promise<void> => {
    if (!activeWorkspace) return;
    try {
      const [nextMembers, nextInvites] = await Promise.all([
        apiRequest<WorkspaceMemberContract[]>(
          `/workspaces/${activeWorkspace.id}/members`
        ),
        canManage
          ? apiRequest<WorkspaceInviteContract[]>(
              `/workspaces/${activeWorkspace.id}/invites`
            )
          : Promise.resolve([]),
      ]);
      setMembers(nextMembers);
      setInvites(nextInvites);
      setError('');
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : t('membersLoadError')
      );
    }
  };
  useEffect(() => {
    const timer = window.setTimeout(() => {
      load().catch(() => undefined);
    }, 0);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspace?.id]);
  const confirmAction = async (): Promise<void> => {
    if (!activeWorkspace || !confirmation) return;
    if (confirmation.kind === 'transfer') {
      await apiRequest(`/workspaces/${activeWorkspace.id}/ownership-transfer`, {
        method: 'POST',
        body: JSON.stringify({ userId: confirmation.member.userId }),
      });
    } else if (confirmation.kind === 'remove') {
      await apiRequest(
        `/workspaces/${activeWorkspace.id}/members/${confirmation.member.id}`,
        { method: 'DELETE' }
      );
    } else {
      await apiRequest(
        `/workspaces/${activeWorkspace.id}/invites/${confirmation.invite.id}`,
        { method: 'DELETE' }
      );
    }
    setConfirmation(null);
    await load();
  };
  if (!activeWorkspace)
    return <p className="text-sm text-slate-500">{t('selectWorkspace')}</p>;
  return (
    <div className="grid gap-8">
      <section>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold text-white">{t('members')}</h2>
            <p className="mt-1 text-sm text-slate-500">
              {t('yourRole', {
                role: t(activeWorkspace.role.toLowerCase()),
              })}
            </p>
          </div>
          <Badge tone="cyan">
            {t('regionLocked', { region: t(ACTIVE_REGION_NAME_I18N_KEY) })}
          </Badge>
        </div>
        {error && (
          <p className="mt-4 rounded-xl bg-rose-400/10 p-3 text-sm text-rose-200">
            {error}
          </p>
        )}
        <div className="mt-5 grid gap-2">
          {members.map(member => {
            const owner = member.role === 'OWNER';
            const self = member.userId === account?.user.id;
            return (
              <article
                className="flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.07] bg-black/15 p-4"
                key={member.id}
              >
                <span className="grid size-9 place-items-center rounded-full bg-white/10 text-xs font-semibold text-white">
                  {member.displayName.slice(0, 2).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <strong className="block truncate text-sm text-white">
                    {member.displayName}
                    {self ? ` · ${t('you')}` : ''}
                  </strong>
                  <small className="text-slate-500">
                    {member.email || t('vkIdentity')}
                  </small>
                </span>
                {canManage && !owner && !self ? (
                  <Select
                    className="w-32"
                    value={member.role}
                    onChange={event =>
                      void apiRequest(
                        `/workspaces/${activeWorkspace.id}/members/${member.id}`,
                        {
                          method: 'PATCH',
                          body: JSON.stringify({ role: event.target.value }),
                        }
                      ).then(load)
                    }
                  >
                    <option value="MEMBER">{t('member')}</option>
                    <option value="ADMIN">{t('admin')}</option>
                  </Select>
                ) : (
                  <Badge tone={owner ? 'cyan' : 'neutral'}>
                    {t(member.role.toLowerCase())}
                  </Badge>
                )}
                {activeWorkspace.role === 'OWNER' && !self && !owner && (
                  <Button
                    variant="ghost"
                    onClick={() =>
                      setConfirmation({ kind: 'transfer', member })
                    }
                  >
                    {t('transferOwnership')}
                  </Button>
                )}
                {canManage && !owner && !self && (
                  <Button
                    variant="danger"
                    onClick={() => setConfirmation({ kind: 'remove', member })}
                  >
                    {t('remove')}
                  </Button>
                )}
              </article>
            );
          })}
        </div>
      </section>
      {canManage && (
        <section className="border-t border-white/[0.07] pt-7">
          <h2 className="font-semibold text-white">{t('invitePeople')}</h2>
          <p className="mt-1 text-sm text-slate-500">{t('inviteExpiryHint')}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Select
              aria-label={t('invitationRole')}
              className="w-36"
              value={inviteRole}
              onChange={event =>
                setInviteRole(event.target.value as 'ADMIN' | 'MEMBER')
              }
            >
              <option value="MEMBER">{t('member')}</option>
              <option value="ADMIN">{t('admin')}</option>
            </Select>
            <Button
              onClick={() =>
                void apiRequest<WorkspaceInviteContract>(
                  `/workspaces/${activeWorkspace.id}/invites`,
                  { method: 'POST', body: JSON.stringify({ role: inviteRole }) }
                ).then(invite => {
                  setLatestLink(invite.inviteUrl || '');
                  void load();
                })
              }
            >
              {t('createSecureLink')}
            </Button>
          </div>
          {latestLink && (
            <div className="mt-4 flex gap-2 rounded-xl border border-cyan-300/15 bg-cyan-300/[0.05] p-3">
              <TextField
                aria-label={t('invitationLink')}
                readOnly
                value={latestLink}
              />
              <Button
                variant="secondary"
                onClick={() => void navigator.clipboard.writeText(latestLink)}
              >
                {t('copy')}
              </Button>
            </div>
          )}
          <div className="mt-4 grid gap-2">
            {invites.map(invite => (
              <div
                className="flex items-center justify-between rounded-xl border border-white/[0.07] p-3 text-sm"
                key={invite.id}
              >
                <span className="text-slate-300">
                  {t(invite.role.toLowerCase())} ·{' '}
                  {t(invite.status.toLowerCase())}
                </span>
                {invite.status === 'PENDING' && (
                  <Button
                    variant="ghost"
                    onClick={() => setConfirmation({ kind: 'revoke', invite })}
                  >
                    {t('revoke')}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
      <AlertDialog
        open={confirmation !== null}
        onOpenChange={open => !open && setConfirmation(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmation?.kind === 'transfer'
                ? t('transferOwnershipTitle')
                : confirmation?.kind === 'remove'
                  ? t('removeMemberTitle')
                  : t('revokeInvitationTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmation?.kind === 'transfer'
                ? t('transferOwnershipDescription', {
                    name: confirmation.member.displayName,
                  })
                : confirmation?.kind === 'remove'
                  ? t('removeMemberDescription', {
                      name: confirmation.member.displayName,
                    })
                  : t('revokeInvitationDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmAction()}>
              {t('confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const SecurityPanel = (): ReactElement => {
  const { t, i18n } = useTranslation('account');
  const api = useRegionalApi();
  const { account, refresh } = useAuth();
  const [sessions, setSessions] = useState<AccountDeviceSession[]>([]);
  const load = async (): Promise<void> =>
    setSessions(await apiRequest<AccountDeviceSession[]>('/auth/sessions'));
  useEffect(() => {
    const timer = window.setTimeout(() => {
      load().catch(() => undefined);
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);
  const providers: OAuthProviderName[] = ['google', 'vk'];
  return (
    <div className="grid gap-8">
      <section>
        <h2 className="font-semibold text-white">{t('providers')}</h2>
        <div className="mt-4 grid gap-2">
          {providers.map(provider => {
            const linked = account?.user.providers.includes(provider);
            const configured = account?.providers.find(
              item => item.provider === provider
            )?.configured;
            return (
              <div
                className="flex items-center justify-between rounded-xl border border-white/[0.07] p-4"
                key={provider}
              >
                <span className="font-semibold uppercase text-white">
                  {provider}
                </span>
                {linked ? (
                  <Button
                    variant="secondary"
                    onClick={() =>
                      void apiRequest(`/auth/identities/${provider}`, {
                        method: 'DELETE',
                      }).then(refresh)
                    }
                  >
                    {t('unlink')}
                  </Button>
                ) : (
                  <Button
                    disabled={!configured}
                    onClick={() =>
                      window.location.assign(
                        api.oauthUrl(provider, '/account', true)
                      )
                    }
                  >
                    {t('link')}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </section>
      <section className="border-t border-white/[0.07] pt-7">
        <h2 className="font-semibold text-white">{t('activeDevices')}</h2>
        <div className="mt-4 grid gap-2">
          {sessions.map(session => (
            <div
              className="flex items-center justify-between gap-4 rounded-xl border border-white/[0.07] p-4"
              key={session.id}
            >
              <span className="min-w-0">
                <strong className="block truncate text-sm text-white">
                  {session.current
                    ? t('thisDevice')
                    : session.userAgent || t('unknownDevice')}
                </strong>
                <small className="text-slate-500">
                  {t('deviceExpires', {
                    date: new Intl.DateTimeFormat(i18n.resolvedLanguage).format(
                      new Date(session.expiresAt)
                    ),
                  })}
                </small>
              </span>
              {!session.current && (
                <Button
                  variant="danger"
                  onClick={() =>
                    void apiRequest(`/auth/sessions/${session.id}`, {
                      method: 'DELETE',
                    }).then(load)
                  }
                >
                  {t('revoke')}
                </Button>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export const DeletedWorkspacesPanel = (): ReactElement => {
  const { t, i18n } = useTranslation('account');
  const { refreshWorkspaces } = useWorkspace();
  const [deleted, setDeleted] = useState<WorkspaceContract[]>([]);
  const load = async (): Promise<void> =>
    setDeleted(
      await apiRequest<WorkspaceContract[]>('/workspaces?deleted=true')
    );
  useEffect(() => {
    const timer = window.setTimeout(() => {
      load().catch(() => undefined);
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);
  return (
    <div>
      <h2 className="font-semibold text-white">{t('deletedWorkspaces')}</h2>
      <p className="mt-2 text-sm text-slate-500">
        {t('deletedWorkspacesHint')}
      </p>
      <div className="mt-5 grid gap-2">
        {deleted.length ? (
          deleted.map(workspace => (
            <div
              className="flex items-center justify-between rounded-xl border border-white/[0.07] p-4"
              key={workspace.id}
            >
              <span>
                <strong className="block text-sm text-white">
                  {workspace.name}
                </strong>
                <small className="text-slate-500">
                  {workspace.purgeAt
                    ? t('purgeDate', {
                        date: new Intl.DateTimeFormat(
                          i18n.resolvedLanguage
                        ).format(new Date(workspace.purgeAt)),
                      })
                    : t('purgePending')}
                </small>
              </span>
              <Button
                onClick={() =>
                  void apiRequest(`/workspaces/${workspace.id}/restore`, {
                    method: 'POST',
                  }).then(async () => {
                    await load();
                    await refreshWorkspaces();
                  })
                }
              >
                {t('restore')}
              </Button>
            </div>
          ))
        ) : (
          <p className="text-sm text-slate-600">{t('noDeletedWorkspaces')}</p>
        )}
      </div>
    </div>
  );
};

export const InviteAcceptPage = (): ReactElement => {
  const { t } = useTranslation('account');
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState('');
  useEffect(() => {
    void apiRequest<{ workspaceId: string }>(
      `/workspace-invites/${token}/accept`,
      {
        method: 'POST',
      }
    )
      .then(async () => {
        await refresh();
        navigate('/', { replace: true });
      })
      .catch(reason =>
        setError(
          reason instanceof Error ? reason.message : t('inviteAcceptError')
        )
      );
  }, [navigate, refresh, t, token]);
  return (
    <main className="grid min-h-screen place-items-center bg-[#090b0f] p-6 text-center text-white">
      <div className="rounded-3xl border border-white/10 bg-[#11161d] p-8">
        <span className="mx-auto mb-5 block size-2 rounded-full bg-cyan-300 shadow-[0_0_24px_8px_rgb(103_230_238_/_0.18)]" />
        <h1 className="text-xl font-semibold">
          {error || t('joiningWorkspace')}
        </h1>
      </div>
    </main>
  );
};
